import { useEffect, useMemo, useRef, useState } from "react"
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  ChevronDown,
  Circle,
  CircleHelp,
  Clock3,
  Download,
  ExternalLink,
  Mail,
  MailCheck,
  Minus,
  TriangleAlert,
  X
} from "lucide-react"
import { csrfToken } from "../lib/rails"
import { AgentActivityFeed, formatEventText } from "./AgentActivityFeed"
import { LoadingOrb } from "./LoadingOrb"
import { RepositoryOnboardingSky } from "./RepositoryOnboardingSky"

const terminalStatuses = new Set(["completed", "failed", "skipped"])
const activeStatuses = new Set(["running", "blocked"])
const knownTaskStatuses = new Set(["queued", "running", "blocked", "completed", "failed", "skipped"])
const onboardingPollTimeout = 10_000
const onboardingTaskOrbStates = {
  mapping_stack: "searching",
  analyzing_repository: "solving",
  processing_history: "searching",
  generating_concepts: "solving",
  generating_context_diagrams: "shaping",
  generating_pull_requests: "composing"
}
const onboardingTaskDisplayGroups = {
  completed: 0,
  running: 1,
  blocked: 1,
  failed: 1,
  queued: 2,
  unknown: 2,
  skipped: 3
}

export function onboardingTaskOrbState(task) {
  const key = typeof task === "string" ? task : task?.key
  return onboardingTaskOrbStates[key] || "working"
}

export function onboardingDisplayTasks(tasks = [], canonicalTasks = tasks) {
  const canonicalPositions = new Map()
  canonicalTasks.forEach((task, index) => {
    if (task?.key != null && !canonicalPositions.has(task.key)) canonicalPositions.set(task.key, index)
  })

  return tasks
    .map((task, index) => ({
      currentPosition: index,
      group: onboardingTaskDisplayGroups[onboardingTaskDisplayStatus(task)] ?? onboardingTaskDisplayGroups.unknown,
      stablePosition: canonicalPositions.get(task?.key) ?? canonicalTasks.length + index,
      task
    }))
    .sort((left, right) => (
      left.group - right.group ||
      left.stablePosition - right.stablePosition ||
      left.currentPosition - right.currentPosition
    ))
    .map(({ task }) => task)
}

export function RepositoryOnboardingProgress({
  conceptCount = 0,
  initialConceptItems = null,
  onDismiss = null,
  onboarding,
  repository,
  returnPath = null,
  variant = "onboarding",
  workspacePaths = {}
}) {
  const { setSnapshot, snapshot } = useRepositoryOnboardingProgress(onboarding)
  const tasks = snapshot?.tasks || []
  const [openTaskKey, setOpenTaskKey] = useState(() => nextOpenTaskKey([], tasks, null))
  const priorTasksRef = useRef(tasks)
  const canonicalTasksRef = useRef(tasks.map((task) => ({ key: task.key })))
  const canonicalTaskKeys = new Set(canonicalTasksRef.current.map((task) => task.key))
  tasks.forEach((task) => {
    if (!canonicalTaskKeys.has(task.key)) {
      canonicalTasksRef.current.push({ key: task.key })
      canonicalTaskKeys.add(task.key)
    }
  })
  const displayTasks = useMemo(
    () => onboardingDisplayTasks(tasks, canonicalTasksRef.current),
    [tasks]
  )

  useEffect(() => {
    setOpenTaskKey((currentKey) => nextOpenTaskKey(priorTasksRef.current, displayTasks, currentKey))
    priorTasksRef.current = displayTasks
  }, [displayTasks])

  // The sky must keep the same fragment position in both branches so React
  // preserves it — and its WebGL scene — across the progress → completion swap.
  const finished = onboardingFinished(snapshot, tasks)
  const sky = (
    <RepositoryOnboardingSky
      finished={finished}
      initialConceptItems={initialConceptItems}
      snapshot={snapshot}
    />
  )

  if (finished) {
    if (variant === "sync") {
      return (
        <>
          {sky}
          <SyncCompletion
            onDismiss={onDismiss}
            onSnapshotChange={setSnapshot}
            repository={repository}
            snapshot={snapshot}
            tasks={displayTasks}
          />
        </>
      )
    }

    return (
      <>
        {sky}
        <OnboardingCompletion
          conceptCount={conceptCount}
          onSnapshotChange={setSnapshot}
          repository={repository}
          returnPath={returnPath}
          snapshot={snapshot}
          tasks={displayTasks}
          workspacePaths={workspacePaths}
        />
      </>
    )
  }

  const completedCount = tasks.filter(taskComplete).length
  const totalCount = snapshot?.totalCount ?? tasks.length
  const repositoryName = repository?.displayName || "your repository"
  const summary = stageSummary({ completedCount, tasks })
  const blocker = repositoryOnboardingBlocker(snapshot, tasks)
  const overallEstimate = blocker ? null : onboardingEstimateLabel({
    estimate: snapshot?.overallEstimate,
    status: snapshot?.active ? "running" : snapshot?.status
  })
  const visibleSummary = [summary, overallEstimate].filter(Boolean).join(" · ")
  const noticesVisible = Boolean(blocker)
  const expandedTask = tasks.find((task) => task.key === openTaskKey)
  const expandedTaskHasContent = Boolean(
    expandedTask && taskPanelHasContent(expandedTask, eventsForTask(snapshot?.events, expandedTask.key))
  )

  return (
    <>
      {sky}
      {variant === "sync" && onDismiss ? (
        <button className="repository-onboarding-back" type="button" onClick={onDismiss}>
          <ArrowLeft aria-hidden="true" />
          Back to your workspace
        </button>
      ) : null}
      <main className="repository-onboarding-page" aria-labelledby="repository-onboarding-title">
        <section className="repository-onboarding-intro">
          <h1 className="repository-onboarding-title" id="repository-onboarding-title">
            {variant === "sync" ?
              `Syncing ${repositoryName}.` :
              `We’re getting to know ${repositoryName}.`}
          </h1>

          {noticesVisible ? (
            <div className="repository-onboarding-notices">
              {blocker ? <OnboardingPausedNotice blocker={blocker} /> : null}
            </div>
          ) : null}

          <div className={`repository-onboarding-stage ${noticesVisible ? "has-notice" : ""}`.trim()}>
            <strong>{visibleSummary}</strong>
            <div className="repository-onboarding-stage-track" role="img" aria-label={summary}>
              {displayTasks.map((task) => {
                const displayStatus = onboardingTaskDisplayStatus(task)
                return (
                  <span
                    className={`repository-onboarding-stage-segment is-${displayStatus}`}
                    key={task.key}
                  />
                )
              })}
            </div>
          </div>

          <ol className="repository-onboarding-tasks" aria-label={`${totalCount} onboarding tasks`}>
            {displayTasks.map((task) => {
              const taskEvents = eventsForTask(snapshot?.events, task.key)
              const expandable = taskPanelHasContent(task, taskEvents)
              const expanded = expandable && openTaskKey === task.key
              const estimateLabel = onboardingEstimateLabel(task)
              const displayStatus = onboardingTaskDisplayStatus(task)
              const detail = onboardingTaskDetail(task)
              const triggerId = `repository-onboarding-${task.key}-trigger`
              const panelId = `repository-onboarding-${task.key}-panel`
              const TaskTrigger = expandable ? "button" : "div"

              return (
                <li
                  className={`repository-onboarding-task is-${displayStatus} ${expanded ? "is-selected" : ""}`.trim()}
                  data-task-key={task.key}
                  data-task-status={displayStatus}
                  key={task.key}
                >
                  <TaskTrigger
                    className={`repository-onboarding-task-button ${expandable ? "" : "is-static"}`.trim()}
                    id={expandable ? triggerId : undefined}
                    type={expandable ? "button" : undefined}
                    aria-controls={expandable ? panelId : undefined}
                    aria-expanded={expandable ? expanded : undefined}
                    onClick={expandable ? () => setOpenTaskKey((currentKey) => toggleOpenTaskKey(currentKey, task.key)) : undefined}
                  >
                    <TaskMarker queuedDot status={displayStatus} />
                    <span className="repository-onboarding-task-copy">
                      <span className="repository-onboarding-task-title">{task.label}</span>
                      {detail ? <span className="repository-onboarding-task-detail">{detail}</span> : null}
                      {estimateLabel ? <span className="repository-onboarding-task-estimate">{estimateLabel}</span> : null}
                    </span>
                    <span className="repository-onboarding-task-state">{taskStateLabel(displayStatus)}</span>
                    {expandable ? <ChevronDown className="repository-onboarding-task-chevron" aria-hidden="true" /> : null}
                  </TaskTrigger>

                  {expandable ? (
                    <TaskPanel
                      events={taskEvents}
                      hidden={!expanded}
                      id={panelId}
                      labelledBy={triggerId}
                      task={task}
                    />
                  ) : null}
                </li>
              )
            })}
          </ol>

          <CompletionNotificationAction
            onSnapshotChange={setSnapshot}
            snapshot={snapshot}
          />

          <p className="sr-only" role="status" aria-live="polite">
            {summary}. {expandedTaskHasContent ? `${expandedTask?.label || "Task"} details open.` : "Preparing task details."}
          </p>
        </section>
      </main>
    </>
  )
}

function CompletionNotificationAction({ onSnapshotChange, snapshot }) {
  const requested = Boolean(snapshot?.completionNotificationRequested)
  const path = snapshot?.completionNotificationPath
  const [requestState, setRequestState] = useState(requested ? "confirmed" : "idle")
  const [requestError, setRequestError] = useState(null)

  useEffect(() => {
    if (requested) setRequestState("confirmed")
  }, [requested])

  if (!path && requestState !== "confirmed") return null

  async function requestNotification() {
    if (requestState === "pending" || requestState === "confirmed") return

    setRequestState("pending")
    setRequestError(null)

    try {
      const update = await requestCompletionNotification(snapshot)
      onSnapshotChange((current) => ({ ...current, ...update }))
      setRequestState("confirmed")
    } catch (error) {
      setRequestError(error.message || "We couldn’t set up the email. Please try again.")
      setRequestState("idle")
    }
  }

  return (
    <CompletionNotificationPrompt
      error={requestError}
      onRequest={requestNotification}
      pending={requestState === "pending"}
      requested={requestState === "confirmed"}
    />
  )
}

export function CompletionNotificationPrompt({ error = null, onRequest, pending = false, requested = false }) {
  if (requested) {
    return (
      <div className="repository-onboarding-notification">
        <p className="repository-onboarding-notification-confirmation" role="status">
          <MailCheck aria-hidden="true" />
          <span className="repository-onboarding-notification-confirmation-copy">
            <strong>We’ll email you when it’s ready.</strong>
            <span>You can close this page.</span>
          </span>
        </p>
      </div>
    )
  }

  return (
    <div className="repository-onboarding-notification">
      <button
        aria-busy={pending ? "true" : undefined}
        className="repository-onboarding-notification-button"
        disabled={pending}
        onClick={onRequest}
        type="button"
      >
        <Mail aria-hidden="true" />
        {pending ? "Setting up email…" : "Email me when it’s ready"}
      </button>
      {error ? <p className="repository-onboarding-notification-error" role="alert">{error}</p> : null}
    </div>
  )
}

export async function requestCompletionNotification(snapshot, {
  authenticityToken = csrfToken(),
  fetcher = fetch
} = {}) {
  const path = snapshot?.completionNotificationPath ?? snapshot?.completion_notification_path
  if (!path) throw new Error("Completion email is unavailable.")

  const response = await fetcher(path, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": authenticityToken
    }
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(payload.error || "We couldn’t set up the email. Please try again.")

  const source = payload?.onboarding || payload
  return {
    completionNotificationPath:
      source?.completionNotificationPath ??
      source?.completion_notification_path ??
      path,
    completionNotificationRequested: Boolean(
      source?.completionNotificationRequested ??
      source?.completion_notification_requested ??
      true
    )
  }
}

export async function retryRepositoryAnalysis(snapshot, {
  authenticityToken = csrfToken(),
  fetcher = fetch
} = {}) {
  const path = snapshot?.retryPath ?? snapshot?.retry_path
  if (!path) throw new Error("Repository analysis retry is unavailable.")

  const response = await fetcher(path, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": authenticityToken
    }
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(payload.error || "We couldn’t restart repository analysis. Please try again.")

  return normalizeSnapshot(payload?.onboarding || payload)
}

function RetryRepositoryAnalysisAction({ onSnapshotChange, snapshot }) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState(null)

  if (!snapshot?.retryPath) return null

  async function retryAnalysis() {
    if (pending) return

    setPending(true)
    setError(null)
    try {
      onSnapshotChange(await retryRepositoryAnalysis(snapshot))
    } catch (requestError) {
      setError(requestError.message || "We couldn’t restart repository analysis. Please try again.")
      setPending(false)
    }
  }

  return (
    <RetryRepositoryAnalysisPrompt
      error={error}
      onRetry={retryAnalysis}
      pending={pending}
    />
  )
}

export function RetryRepositoryAnalysisPrompt({ error = null, onRetry, pending = false }) {
  return (
    <div className="repository-onboarding-notification">
      <button
        aria-busy={pending ? "true" : undefined}
        className="repository-onboarding-notification-button"
        disabled={pending}
        onClick={onRetry}
        type="button"
      >
        <ArrowRight aria-hidden="true" />
        {pending ? "Starting fresh analysis…" : "Retry repository analysis"}
      </button>
      {error ? <p className="repository-onboarding-notification-error" role="alert">{error}</p> : null}
    </div>
  )
}

function OnboardingCompletion({ conceptCount, onSnapshotChange, repository, returnPath, snapshot, tasks, workspacePaths }) {
  const [leavingPath, setLeavingPath] = useState(null)
  const completionHeadingRef = useRef(null)
  const navigationTimerRef = useRef(null)
  const failedTasks = tasks.filter((task) => task.status === "failed")
  const spotAnalysisInterrupted = failedTasks.some((task) => (
    task.key === "analyzing_repository" && task.failureCode === "spot_interrupted"
  ))
  const failedDiagrams = failedTasks.flatMap((task) => task.diagrams || [])
    .filter((diagram) => contextDiagramDisplayStatus(diagram) === "failed")
  const degraded = snapshot?.status === "failed" || failedTasks.length > 0
  const attentionCount = failedTasks.length || 1
  const completedCount = tasks.filter(taskComplete).length
  const skippedCount = tasks.filter((task) => task.status === "skipped").length
  const finishedCount = tasks.filter((task) => taskTerminal(task) && task.status !== "skipped").length
  const totalCount = snapshot?.totalCount || tasks.length
  const repositoryName = repository?.displayName || "your repository"
  const activeSection = repository?.activeSection || "concept"
  const requestedPath = returnPath || currentWorkspacePath() || workspacePaths?.[activeSection] || snapshot?.repositoryPath
  const conceptPath = activeSection === "concept" ? requestedPath : workspacePaths?.concept || snapshot?.repositoryPath
  const contextPath = activeSection === "domains" ? requestedPath : workspacePaths?.domains
  const conceptTask = tasks.find((task) => task.key === "generating_concepts")
  const resolvedConceptCount = onboardingConceptCount(conceptTask, conceptCount)
  const conceptsReady = resolvedConceptCount > 0 && conceptTask?.status === "completed"
  const destinations = [conceptCompletionDestination(conceptPath, resolvedConceptCount, true)]
  const resumeDestination = completionResumeDestination(activeSection, requestedPath)

  useEffect(() => {
    completionHeadingRef.current?.focus({ preventScroll: true })

    return () => {
      if (navigationTimerRef.current) window.clearTimeout(navigationTimerRef.current)
    }
  }, [])

  function transitionToWorkspace(event, path) {
    if (!path || leavingPath || event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    event.preventDefault()
    setLeavingPath(path)
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    navigationTimerRef.current = window.setTimeout(() => window.location.replace(path), reducedMotion ? 0 : 180)
  }

  return (
    <main
      className={`repository-onboarding-page is-complete ${leavingPath ? "is-leaving" : ""}`.trim()}
      aria-busy={Boolean(leavingPath)}
      aria-labelledby="repository-onboarding-complete-title"
    >
      <section className="repository-onboarding-completion">
        <div
          className="repository-onboarding-completion-track"
          role="img"
          aria-label={[
            `${completedCount} onboarding ${completedCount === 1 ? "step" : "steps"} complete`,
            degraded ? `${attentionCount} ${attentionCount === 1 ? "needs" : "need"} attention` : null,
            skippedCount ? `${skippedCount} skipped` : null
          ].filter(Boolean).join(", ")}
        >
          {tasks.map((task) => (
            <span className={`is-${onboardingTaskDisplayStatus(task)}`} key={task.key} />
          ))}
        </div>

        <div className={`repository-onboarding-completion-mark ${degraded ? "is-degraded" : ""}`.trim()} aria-hidden="true">
          <Check />
        </div>
        <p className="repository-onboarding-completion-kicker">
          {degraded
            ? [
                `${finishedCount} ${finishedCount === 1 ? "step" : "steps"} finished`,
                `${attentionCount} ${attentionCount === 1 ? "needs" : "need"} attention`,
                skippedCount ? `${skippedCount} skipped` : null
              ].filter(Boolean).join(" · ")
            : [
                `${completedCount} of ${totalCount} steps complete`,
                skippedCount ? `${skippedCount} skipped` : null
              ].filter(Boolean).join(" · ")}
        </p>
        <h1
          className="repository-onboarding-completion-title"
          id="repository-onboarding-complete-title"
          ref={completionHeadingRef}
          tabIndex={-1}
        >
          {spotAnalysisInterrupted
            ? "Repository analysis was interrupted."
            : degraded ? "Onboarding needs attention." : "Your workspace is ready."}
        </h1>
        <p className="repository-onboarding-completion-copy">
          {spotAnalysisInterrupted
            ? "EC2 Spot interrupted both automatic analysis attempts. Completed setup work was kept; start a fresh analysis to continue."
            : degraded
            ? `${attentionCount === 1 ? "One onboarding step needs" : `${attentionCount} onboarding steps need`} attention before your workspace is ready. Review the details below.`
            : conceptsReady
              ? `Your concepts, context, stack, and first findings for ${repositoryName} are ready to explore.`
              : `Your context, stack, and first findings for ${repositoryName} are ready to explore.`}
        </p>

        <RetryRepositoryAnalysisAction
          onSnapshotChange={onSnapshotChange}
          snapshot={snapshot}
        />

        {failedDiagrams.length > 0 ? (
          <div className="repository-onboarding-notices">
            {failedDiagrams.map((diagram, index) => (
              <div className="repository-onboarding-notice is-stale" key={contextDiagramId(diagram) || index} role="status">
                <TriangleAlert aria-hidden="true" />
                <span className="repository-onboarding-notice-copy">
                  <strong>{contextDiagramFailureTitle(diagram)}</strong>
                  <span>{contextDiagramFailureDetail(diagram)}</span>
                </span>
                {contextPath ? (
                  <a
                    className="repository-onboarding-notice-action"
                    href={contextPath}
                    onClick={(event) => transitionToWorkspace(event, contextPath)}
                  >
                    Open Context
                    <ArrowRight aria-hidden="true" />
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {degraded ? null : resumeDestination ? (
          <>
            <a
              className="repository-onboarding-resume"
              href={resumeDestination.path}
              onClick={(event) => transitionToWorkspace(event, resumeDestination.path)}
            >
              <span>
                <small>Continue where you left off</small>
                <strong>Open {resumeDestination.label}</strong>
              </span>
              <ArrowRight aria-hidden="true" />
            </a>
            <p className="repository-onboarding-choice-label">Or choose a starting point</p>
          </>
        ) : (
          <p className="repository-onboarding-choice-label">Choose where to start</p>
        )}

        {!degraded ? <nav className="repository-onboarding-destinations" aria-label="Choose where to start">
          {destinations.map((destination) => (
            <CompletionDestination
              destination={destination}
              key={destination.key}
              onNavigate={transitionToWorkspace}
            />
          ))}
        </nav> : null}

        {!degraded && repository?.agentSetupPath ? (
          <section className="repository-onboarding-agent-context" aria-labelledby="repository-onboarding-agent-context-title">
            <span className="repository-onboarding-agent-context-icon" aria-hidden="true"><Download /></span>
            <span className="repository-onboarding-agent-context-copy">
              <small>Optional agent setup</small>
              <strong id="repository-onboarding-agent-context-title">Bring this context into Codex or Claude Code</strong>
              <span>Download reviewed MCP, repository marker, and SessionStart files. OAuth is completed in your browser.</span>
            </span>
            <a href={repository.agentSetupPath} download>
              Download agent context
            </a>
          </section>
        ) : null}

        <details className="repository-onboarding-completion-details" open={degraded}>
          <summary>
            <span>View setup details</span>
            <ChevronDown aria-hidden="true" />
          </summary>
          <ol aria-label="Finished onboarding steps">
            {tasks.map((task) => {
              const displayStatus = onboardingTaskDisplayStatus(task)
              return (
                <li className={`is-${displayStatus}`} key={task.key}>
                  <TaskMarker status={displayStatus} small />
                  <span>
                    <strong>{task.label}</strong>
                    {task.error || task.detail ? <small>{task.error || task.detail}</small> : null}
                  </span>
                  <em>{taskStateLabel(displayStatus)}</em>
                </li>
              )
            })}
          </ol>
        </details>

        <p className="sr-only" role="status" aria-live="polite">
          {degraded ? "Onboarding needs attention. " : "Onboarding finished. "}{skippedCount ? `${skippedCount} ${skippedCount === 1 ? "item was" : "items were"} skipped. ` : ""}{degraded ? `${attentionCount} ${attentionCount === 1 ? "item needs" : "items need"} attention. Review setup details.` : "Choose a workspace destination."}
        </p>
      </section>
    </main>
  )
}

function SyncCompletion({ onDismiss = null, onSnapshotChange, repository, snapshot, tasks }) {
  const completionHeadingRef = useRef(null)
  const failedTasks = tasks.filter((task) => task.status === "failed")
  const degraded = snapshot?.status === "failed" || failedTasks.length > 0
  const attentionCount = failedTasks.length || 1
  const completedCount = tasks.filter(taskComplete).length
  const skippedCount = tasks.filter((task) => task.status === "skipped").length
  const totalCount = snapshot?.totalCount || tasks.length
  const repositoryName = repository?.displayName || "your repository"
  const failedTask = failedTasks[0]
  const spotAnalysisInterrupted = failedTasks.some((task) => (
    task.key === "analyzing_repository" && task.failureCode === "spot_interrupted"
  ))

  useEffect(() => {
    completionHeadingRef.current?.focus({ preventScroll: true })
  }, [])

  return (
    <main
      className="repository-onboarding-page is-complete"
      aria-labelledby="repository-onboarding-complete-title"
    >
      <section className="repository-onboarding-completion">
        <div
          className="repository-onboarding-completion-track"
          role="img"
          aria-label={[
            `${completedCount} sync ${completedCount === 1 ? "step" : "steps"} complete`,
            degraded ? `${attentionCount} ${attentionCount === 1 ? "needs" : "need"} attention` : null,
            skippedCount ? `${skippedCount} skipped` : null
          ].filter(Boolean).join(", ")}
        >
          {tasks.map((task) => (
            <span className={`is-${onboardingTaskDisplayStatus(task)}`} key={task.key} />
          ))}
        </div>

        <div className={`repository-onboarding-completion-mark ${degraded ? "is-degraded" : ""}`.trim()} aria-hidden="true">
          <Check />
        </div>
        <p className="repository-onboarding-completion-kicker">
          {degraded
            ? [
                `${completedCount} ${completedCount === 1 ? "step" : "steps"} complete`,
                `${attentionCount} ${attentionCount === 1 ? "needs" : "need"} attention`,
                skippedCount ? `${skippedCount} skipped` : null
              ].filter(Boolean).join(" · ")
            : [
                `${completedCount} of ${totalCount} steps complete`,
                skippedCount ? `${skippedCount} skipped` : null
              ].filter(Boolean).join(" · ")}
        </p>
        <h1
          className="repository-onboarding-completion-title"
          id="repository-onboarding-complete-title"
          ref={completionHeadingRef}
          tabIndex={-1}
        >
          {spotAnalysisInterrupted
            ? "Repository analysis was interrupted."
            : degraded ? "Sync needs attention." : "Your workspace is up to date."}
        </h1>
        <p className="repository-onboarding-completion-copy">
          {spotAnalysisInterrupted
            ? "EC2 Spot interrupted both automatic analysis attempts. Completed sync work was kept; start a fresh analysis to continue."
            : degraded
            ? `${failedTask?.label || "One step"} could not finish${failedTask?.detail || failedTask?.error ? `: ${failedTask.detail || failedTask.error}` : "."}`
            : `The latest code, context, and concepts for ${repositoryName} are ready to explore.`}
        </p>

        <RetryRepositoryAnalysisAction
          onSnapshotChange={onSnapshotChange}
          snapshot={snapshot}
        />

        <button
          className="repository-onboarding-resume"
          type="button"
          onClick={() => (onDismiss ? onDismiss() : window.location.reload())}
        >
          <span>
            <small>{degraded ? "Return and try again" : "Continue where you left off"}</small>
            <strong>Back to your workspace</strong>
          </span>
          <ArrowRight aria-hidden="true" />
        </button>

        <details className="repository-onboarding-completion-details">
          <summary>
            <span>View sync details</span>
            <ChevronDown aria-hidden="true" />
          </summary>
          <ol aria-label="Finished sync steps">
            {tasks.map((task) => {
              const displayStatus = onboardingTaskDisplayStatus(task)
              return (
                <li className={`is-${displayStatus}`} key={task.key}>
                  <TaskMarker status={displayStatus} small />
                  <span>
                    <strong>{task.label}</strong>
                    {task.error || task.detail ? <small>{task.error || task.detail}</small> : null}
                  </span>
                  <em>{taskStateLabel(displayStatus)}</em>
                </li>
              )
            })}
          </ol>
        </details>

        <p className="sr-only" role="status" aria-live="polite">
          Sync finished. {degraded ? `${attentionCount} ${attentionCount === 1 ? "step needs" : "steps need"} attention.` : "All steps complete."}
        </p>
      </section>
    </main>
  )
}

export function onboardingConceptCount(task, fallback = 0) {
  const taskCount = task?.count ?? task?.counts?.selectedConceptCount ?? task?.counts?.selected_concept_count
  const count = taskCount === null || taskCount === undefined ? Number(fallback) : Number(taskCount)
  return Number.isFinite(count) && count >= 0 ? count : 0
}

function CompletionDestination({ destination, onNavigate }) {
  const Icon = destination.icon

  return (
    <a
      className={`repository-onboarding-destination ${destination.primary ? "is-primary" : ""}`.trim()}
      href={destination.path}
      onClick={(event) => onNavigate(event, destination.path)}
    >
      <span className="repository-onboarding-destination-icon" aria-hidden="true"><Icon /></span>
      <span className="repository-onboarding-destination-copy">
        <small>{destination.eyebrow}</small>
        <strong>{destination.title}</strong>
        <span>{destination.description}</span>
      </span>
      <span className="repository-onboarding-destination-meta">
        {destination.meta}
        <ArrowRight aria-hidden="true" />
      </span>
    </a>
  )
}

function conceptCompletionDestination(path, count, primary) {
  return {
    key: "concept",
    title: "Explore concepts",
    eyebrow: primary ? "Your next step" : "Start with the big picture",
    description: "See the product ideas and system story your code has grown around.",
    meta: count > 0 ? `${count} ${count === 1 ? "concept" : "concepts"}` : "Product story",
    icon: BookOpen,
    path,
    primary
  }
}

function completionResumeDestination(activeSection, path) {
  const labels = {
    domains: "Context",
    stack: "Stack",
    team: "Team"
  }
  const label = labels[activeSection]
  return label && path ? { label, path } : null
}

function currentWorkspacePath() {
  if (typeof window === "undefined" || !window.location?.pathname) return null
  return `${window.location.pathname}${window.location.search || ""}${window.location.hash || ""}`
}

function OnboardingPausedNotice({ blocker }) {
  return (
    <div className="repository-onboarding-notice is-paused" role="status">
      <TriangleAlert aria-hidden="true" />
      <span className="repository-onboarding-notice-copy">
        <strong>{blocker.title}</strong>
        <span>{blocker.detail}</span>
      </span>
    </div>
  )
}

export function useRepositoryOnboardingProgress(initialOnboarding) {
  const [snapshot, setSnapshot] = useState(() => normalizeSnapshot(initialOnboarding))
  const cursorRef = useRef(initialOnboarding?.nextCursor ?? initialOnboarding?.next_cursor ?? 0)
  const hasMoreRef = useRef(Boolean(initialOnboarding?.hasMore ?? initialOnboarding?.has_more))
  const timerRef = useRef(null)
  const activeRequestRef = useRef(null)

  useEffect(() => {
    setSnapshot(normalizeSnapshot(initialOnboarding))
    cursorRef.current = initialOnboarding?.nextCursor ?? initialOnboarding?.next_cursor ?? 0
    hasMoreRef.current = Boolean(initialOnboarding?.hasMore ?? initialOnboarding?.has_more)
  }, [initialOnboarding?.id])

  // Prop-driven mode: without a statusPath there is nothing to poll, so the
  // parent-owned snapshot object is the source of truth and every update to it
  // must be reflected (the id stays constant across those updates).
  // Hybrid mode: with a statusPath the event stream and cursor are poll-owned,
  // but the parent still owns task/status fields (sync-now orchestrates the
  // phases client-side), so those merge in on every prop update.
  useEffect(() => {
    const incoming = normalizeSnapshot(initialOnboarding)
    if (!incoming) return

    if (!incoming.statusPath) {
      setSnapshot(incoming)
      return
    }

    setSnapshot((current) => {
      if (!current) return incoming

      return {
        ...current,
        status: incoming.status,
        active: incoming.active,
        completedCount: incoming.completedCount,
        runningCount: incoming.runningCount,
        skippedCount: incoming.skippedCount,
        totalCount: incoming.totalCount,
        tasks: incoming.tasks,
        statusPath: incoming.statusPath ?? current.statusPath,
        conceptsPath: incoming.conceptsPath ?? current.conceptsPath
      }
    })
  }, [initialOnboarding])

  useEffect(() => {
    const statusPath = snapshot?.statusPath || initialOnboarding?.statusPath
    if (!statusPath || !snapshot?.active) return undefined
    let stopped = false

    function clearTimer() {
      if (timerRef.current) window.clearTimeout(timerRef.current)
      timerRef.current = null
    }

    function schedule(delay) {
      clearTimer()
      if (!stopped) timerRef.current = window.setTimeout(poll, delay)
    }

    async function poll() {
      if (stopped || activeRequestRef.current) return

      const separator = statusPath.includes("?") ? "&" : "?"
      const controller = new AbortController()
      let timedOut = false
      const timeoutId = window.setTimeout(() => {
        timedOut = true
        controller.abort()
      }, onboardingPollTimeout)
      activeRequestRef.current = controller

      try {
        const response = await fetch(`${statusPath}${separator}after=${encodeURIComponent(cursorRef.current || 0)}`, {
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "Cache-Control": "no-store"
          },
          signal: controller.signal
        })
        if (!response.ok) throw new Error(`Onboarding status request failed (${response.status}).`)

        const payload = await response.json()
        const incoming = normalizeSnapshot(payload?.onboarding || payload)
        cursorRef.current = incoming?.nextCursor ?? cursorRef.current
        hasMoreRef.current = Boolean(incoming?.hasMore)
        setSnapshot((current) => mergeOnboardingSnapshots(current, incoming))

        const delay = nextOnboardingPollDelay({
          active: incoming?.active,
          hasMore: incoming?.hasMore,
          hidden: document.hidden
        })
        if (delay !== null) schedule(delay)
      } catch (error) {
        if (!stopped && (error.name !== "AbortError" || timedOut)) {
          schedule(document.hidden ? 15000 : 1000)
        }
      } finally {
        window.clearTimeout(timeoutId)
        if (activeRequestRef.current === controller) activeRequestRef.current = null
      }
    }

    function handleVisibilityChange() {
      clearTimer()
      if (document.hidden) {
        schedule(hasMoreRef.current ? 0 : 15000)
      } else {
        poll()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    poll()

    return () => {
      stopped = true
      clearTimer()
      activeRequestRef.current?.abort()
      activeRequestRef.current = null
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [initialOnboarding?.statusPath, snapshot?.active, snapshot?.id, snapshot?.statusPath])

  return {
    snapshot,
    setSnapshot
  }
}

export function nextOnboardingPollDelay({ active, hasMore, hidden }) {
  if (!active) return null
  if (hasMore) return 0
  return hidden ? 15000 : 1000
}

export function repositoryOnboardingBlocker(snapshot, tasks = snapshot?.tasks || []) {
  if (snapshot?.active === false) return null

  const blocker = snapshot?.blocker
  if (blocker) {
    const missingProviders = blocker.missingProviders || blocker.missing_providers || []
    return {
      key: blocker.key || "onboarding_blocked",
      title: blocker.title || "Onboarding paused",
      detail: blocker.detail || missingProviderDetail(missingProviders),
      missingProviders,
      detectedAt: blocker.detectedAt || blocker.detected_at
    }
  }

  const blockedTask = tasks.find((task) => task?.status === "blocked")
  if (!blockedTask) return null

  return {
    key: taskBlockedBy(blockedTask) || blockedTask.key || "onboarding_blocked",
    title: "Onboarding paused",
    detail: taskBlockedReason(blockedTask) || blockedTask.detail || "A required service is not available yet.",
    missingProviders: []
  }
}

export function onboardingTaskDisplayStatus(task) {
  const status = task?.status
  if (!knownTaskStatuses.has(status)) return "unknown"
  if (!terminalStatuses.has(status) && taskBlockedBy(task)) return "blocked"
  return status
}

export function onboardingTaskDetail(task) {
  const status = task?.status
  const detail = task?.detail
  const blockedReason = taskBlockedReason(task)
  if (!knownTaskStatuses.has(status)) return "Status unavailable. Progress updates will continue."
  if (!terminalStatuses.has(status) && taskBlockedBy(task)) return blockedReason || "Waiting for a required service."
  if (status === "blocked") return blockedReason || detail || "Waiting for a required service."
  if (status === "queued") return null
  if (status === "running") return detail || "Work is in progress."
  return detail
}

function taskBlockedBy(task) {
  return task?.blockedBy || task?.blocked_by
}

function taskBlockedReason(task) {
  return task?.blockedReason || task?.blocked_reason
}

function missingProviderDetail(providers) {
  const labels = Array.from(providers).map((provider) => provider === "claude" ? "Claude Code" : provider === "codex" ? "Codex" : String(provider))
  if (labels.length === 0) return "A required credential is not available yet."
  if (labels.length === 1) return `Waiting for the ${labels[0]} credential.`
  return `Waiting for the ${labels.slice(0, -1).join(", ")} and ${labels.at(-1)} credentials.`
}

export function mergeOnboardingEvents(currentEvents = [], incomingEvents = []) {
  const eventsByCursor = new Map()
  const cursorlessEvents = new Map()

  ;[...currentEvents, ...incomingEvents].forEach((event) => {
    const normalized = normalizeOnboardingEvent(event)
    if (!normalized) return

    if (normalized.cursor > 0) {
      eventsByCursor.set(normalized.cursor, normalized)
    } else {
      cursorlessEvents.set(normalized.key, normalized)
    }
  })

  return [...eventsByCursor.values(), ...cursorlessEvents.values()]
    .sort((left, right) => (left.cursor || 0) - (right.cursor || 0))
    .reduce((events, event) => {
      const previous = events.at(-1)
      const mergeKey = onboardingEventDeltaMergeKey(event)

      if (mergeKey && previous && onboardingEventsShareDeltaStream(previous, event, mergeKey)) {
        events[events.length - 1] = {
          ...previous,
          ...event,
          cursor: Math.max(previous.cursor || 0, event.cursor || 0),
          deltaStream: true,
          key: previous.key,
          mergeKey,
          text: `${previous.text || ""}${event.text || ""}`
        }
        return events
      }

      events.push(event)
      return events
    }, [])
}

export function nextOpenTaskKey(previousTasks = [], nextTasks = [], currentKey = null) {
  if (nextTasks.length === 0) return null
  if (!currentKey) {
    if (previousTasks.length > 0) return null

    return preferredOpenTask(nextTasks)?.key || nextTasks[0].key
  }

  const currentTask = nextTasks.find((task) => task.key === currentKey)
  if (!currentTask) return preferredOpenTask(nextTasks)?.key || nextTasks[0].key

  const previousTask = previousTasks.find((task) => task.key === currentKey)
  const becameTerminal = previousTask && !taskTerminal(previousTask) && taskTerminal(currentTask)
  if (!becameTerminal) return currentKey

  const currentIndex = nextTasks.findIndex((task) => task.key === currentKey)
  return nextTasks.slice(currentIndex + 1).find(taskInProgress)?.key ||
    nextTasks.slice(currentIndex + 1).find((task) => !taskTerminal(task))?.key ||
    nextTasks.find(taskInProgress)?.key ||
    currentKey
}

export function toggleOpenTaskKey(currentKey, clickedKey) {
  return currentKey === clickedKey ? null : clickedKey
}

export function onboardingEstimateLabel(task) {
  if (taskBlockedBy(task)) return null
  if (task?.status !== "queued" && task?.status !== "running") return null

  const estimate = task?.estimate
  if (!estimate) return null
  if (estimate.state === "long_running") {
    return task.status === "running" ? "Taking longer than usual" : null
  }
  if (estimate.state !== "estimated") return null

  const lowSeconds = Number(estimate.lowSeconds)
  const highSeconds = Number(estimate.highSeconds)
  if (!Number.isFinite(lowSeconds) || !Number.isFinite(highSeconds)) return null
  if (lowSeconds < 0 || highSeconds < 0 || lowSeconds > highSeconds) return null

  const lowMinutes = Math.max(1, Math.ceil(lowSeconds / 60))
  const highMinutes = Math.max(1, Math.ceil(highSeconds / 60))
  let duration
  if (lowSeconds <= 3_600) {
    duration = lowMinutes === highMinutes ?
      `${lowMinutes} ${lowMinutes === 1 ? "minute" : "minutes"}` :
      `${lowMinutes}–${highMinutes} minutes`
  } else {
    const lowHours = Math.ceil(Math.max(0.5, Math.round((lowSeconds / 3_600) * 2) / 2))
    const highHours = Math.ceil(Math.max(0.5, Math.round((highSeconds / 3_600) * 2) / 2))
    duration = lowHours === highHours ?
      `${lowHours} ${lowHours === 1 ? "hour" : "hours"}` :
      `${lowHours}–${highHours} hours`
  }

  return task.status === "queued" ? `Usually about ${duration}` : `About ${duration} left`
}

function taskPanelHasContent(task, events = []) {
  const current = task.processedCount ?? task.readyCount ?? task.currentCount ?? task.counts?.readyCount ?? task.count
  const total = task.totalCount ?? task.counts?.totalCount
  const exactProgressAvailable = current !== null && current !== undefined && Number.isFinite(Number(current)) && Number(total) > 0
  const evidenceLists = [
    task.outputs || [],
    task.diagrams || [],
    task.components || task.stack || [],
    task.issues || task.findings || [],
    task.narratives || task.recentNarratives || [],
    task.children || task.pullRequests || []
  ]

  return events.length > 0 || exactProgressAvailable || evidenceLists.some((items) => items.length > 0) || Boolean(task.error)
}

function TaskPanel({ events, hidden, id, labelledBy, task }) {
  const displayStatus = onboardingTaskDisplayStatus(task)
  const contextDiagrams = task.key === "generating_context_diagrams" ? task.diagrams || [] : []

  return (
    <div className="repository-onboarding-task-panel" hidden={hidden} id={id} role="region" aria-labelledby={labelledBy}>
      {contextDiagrams.length > 0 ? (
        <ContextDiagramWorkers
          diagrams={contextDiagrams}
          events={events}
          hidden={hidden}
          streamRegionId={`${id}-worker-stream`}
          taskError={task.error}
        />
      ) : (
        <>
          <TaskEvidence task={task} />
          {events.length > 0 ? (
            <AgentActivityFeed
              className="repository-onboarding-stream"
              events={events}
              generating={displayStatus === "running"}
              hidden={hidden}
              orbState={onboardingTaskOrbState(task)}
              transcript
            />
          ) : null}
        </>
      )}
    </div>
  )
}

function ContextDiagramWorkers({ diagrams, events, hidden, streamRegionId, taskError }) {
  const [selectedDiagramId, setSelectedDiagramId] = useState(() => preferredContextDiagramId(diagrams, events))

  useEffect(() => {
    setSelectedDiagramId((currentDiagramId) => nextContextDiagramId({
      diagrams,
      events,
      selectedDiagramId: currentDiagramId
    }))
  }, [diagrams, events])

  const selectedDiagram = diagrams.find((diagram) => (
    contextDiagramSelectable(diagram) && contextDiagramId(diagram) === selectedDiagramId
  ))
  const activeDiagramId = contextDiagramId(selectedDiagram) || null
  const selectedStreamEvents = useMemo(
    () => contextDiagramFeedEvents(selectedDiagram),
    [selectedDiagram]
  )
  const selectedStatus = contextDiagramDisplayStatus(selectedDiagram)

  function selectDiagram(diagramId) {
    setSelectedDiagramId(diagramId)
  }

  return (
    <>
      <ContextDiagramWorkerList
        diagrams={diagrams}
        onSelect={selectDiagram}
        selectedDiagramId={activeDiagramId}
        streamRegionId={streamRegionId}
      />
      {taskError ? <p className="repository-onboarding-task-error">{taskError}</p> : null}
      {selectedDiagram ? (
        <section
          className="repository-onboarding-diagram-stream-region"
          id={streamRegionId}
          role="region"
          aria-label={`${selectedDiagram.label || "Context diagram"} live activity`}
        >
          {selectedStreamEvents.length > 0 ? (
            <AgentActivityFeed
              className="repository-onboarding-stream repository-onboarding-diagram-stream"
              events={selectedStreamEvents}
              generating={selectedStatus === "running"}
              hidden={hidden}
              key={activeDiagramId}
              orbState="composing"
              transcript
            />
          ) : (
            <ContextDiagramWaitingState diagram={selectedDiagram} />
          )}
        </section>
      ) : null}
    </>
  )
}

function ContextDiagramWorkerList({ diagrams, onSelect, selectedDiagramId, streamRegionId }) {
  return (
    <ol className="repository-onboarding-output-list repository-onboarding-diagram-list" aria-label="Context diagrams">
      {diagrams.map((diagram, index) => {
        const diagramId = contextDiagramId(diagram)
        const displayStatus = contextDiagramDisplayStatus(diagram)
        const selectable = contextDiagramSelectable(diagram)
        const selected = selectable && diagramId === selectedDiagramId
        const detail = contextDiagramWorkerDetail(diagram)
        const content = (
          <>
            <TaskMarker queuedDot status={contextDiagramMarkerStatus(diagram)} small />
            <span className="repository-onboarding-output-copy">
              <strong>{diagram.label || diagram.title || diagram.name}</strong>
              {detail ? <span>{detail}</span> : null}
            </span>
            <span className="repository-onboarding-output-state">{contextDiagramStateLabel(diagram)}</span>
          </>
        )

        return (
          <li
            className={`repository-onboarding-output-row repository-onboarding-diagram-row is-${displayStatus} ${selected ? "is-selected" : ""}`.trim()}
            key={diagramId || index}
          >
            <button
              className="repository-onboarding-diagram-worker-button"
              type="button"
              aria-controls={selectable ? streamRegionId : undefined}
              aria-pressed={selectable ? selected : undefined}
              disabled={!selectable}
              onClick={selectable ? () => onSelect(diagramId) : undefined}
            >
              {content}
            </button>
          </li>
        )
      })}
    </ol>
  )
}

function ContextDiagramWaitingState({ diagram }) {
  const status = contextDiagramDisplayStatus(diagram)
  const copy = contextDiagramWaitingCopy(diagram)

  return (
    <div className={`repository-onboarding-diagram-waiting is-${status}`} role="status">
      <TaskMarker queuedDot status={contextDiagramMarkerStatus(diagram)} small />
      <span>
        <strong>{copy.title}</strong>
        <span>{copy.detail}</span>
      </span>
    </div>
  )
}

function contextDiagramWaitingCopy(diagram) {
  const status = contextDiagramDisplayStatus(diagram)

  if (contextDiagramTimedOut(diagram)) {
    return {
      title: contextDiagramFailureTitle(diagram),
      detail: contextDiagramFailureDetail(diagram)
    }
  }
  if (status === "failed") {
    return {
      title: contextDiagramFailureTitle(diagram),
      detail: contextDiagramFailureDetail(diagram)
    }
  }
  if (status === "running" && contextDiagramWorkerStatus(diagram) === "succeeded") {
    return {
      title: "Finalizing context diagram",
      detail: "The model finished. Validating and saving the diagram now."
    }
  }
  if (status === "running" && contextDiagramWorkerStatus(diagram) === "queued") {
    return {
      title: "Waiting for a diagram worker",
      detail: "Generation will start when a worker is available."
    }
  }
  if (status === "running" && contextDiagramWorkerStatus(diagram) === "starting") {
    return {
      title: "Worker is starting",
      detail: "Waiting for model activity."
    }
  }
  if (status === "running" && contextDiagramWorkerStatus(diagram) === "waiting_for_user") {
    return {
      title: "Worker is waiting for input",
      detail: "Generation will continue after the worker receives the required input."
    }
  }
  if (status === "running" && contextDiagramWorkerStatus(diagram) === "cancelling") {
    return {
      title: "Stopping diagram generation",
      detail: "Waiting for the worker to stop safely."
    }
  }
  if (status === "running") {
    return {
      title: "Worker is running",
      detail: "Waiting for model activity."
    }
  }
  if (status === "blocked") {
    return {
      title: "Waiting for a prerequisite",
      detail: "Live activity will appear after this diagram can start."
    }
  }
  if (status === "queued") {
    return {
      title: "Waiting for a diagram worker",
      detail: "Generation will start when a worker is available."
    }
  }
  if (status === "completed") {
    return {
      title: "Context diagram ready",
      detail: "No retained activity transcript is available."
    }
  }

  return {
    title: "No live activity available",
    detail: "This diagram does not have a retained activity transcript."
  }
}

function contextDiagramId(diagram) {
  return String(diagram?.id ?? "")
}

function contextDiagramEventId(event) {
  return String(event?.diagramId ?? "")
}

function contextDiagramSelectable(diagram) {
  return contextDiagramDisplayStatus(diagram) !== "queued" && contextDiagramWorkerStatus(diagram) !== "queued"
}

export function preferredContextDiagramId(diagrams = [], events = []) {
  const diagramIdsWithEvents = new Set(events.map(contextDiagramEventId).filter(Boolean))
  const activeWithRetainedEvents = diagrams.find((diagram) => (
    contextDiagramDisplayStatus(diagram) === "running" && contextDiagramStreamEvents(diagram).length > 0
  ))
  const activeWithEvents = diagrams.find((diagram) => (
    contextDiagramDisplayStatus(diagram) === "running" &&
    diagramIdsWithEvents.has(contextDiagramId(diagram))
  ))
  const activeDiagram = diagrams.find((diagram) => contextDiagramDisplayStatus(diagram) === "running")
  const failedDiagram = diagrams.find((diagram) => contextDiagramDisplayStatus(diagram) === "failed")
  const firstSelectableDiagramId = diagrams
    .filter(contextDiagramSelectable)
    .map(contextDiagramId)
    .find(Boolean)

  return contextDiagramId(activeWithRetainedEvents) ||
    contextDiagramId(activeWithEvents) ||
    contextDiagramId(activeDiagram) ||
    contextDiagramId(failedDiagram) ||
    firstSelectableDiagramId ||
    null
}

function contextDiagramStreamEvents(diagram) {
  const events = diagram?.streamEvents ?? diagram?.stream_events
  return Array.isArray(events) ? events : []
}

function contextDiagramFeedEvents(diagram) {
  const events = contextDiagramStreamEvents(diagram).map((event, index) => ({
    ...event,
    key: event.key || `diagram-${contextDiagramId(diagram)}-${event.sequence || index}`
  }))

  if (contextDiagramDisplayStatus(diagram) !== "failed") return events

  const terminalText = contextDiagramFailureDetail(diagram)
  const hasTerminalText = events.some((event) => (
    formatEventText(event?.text || event?.rawType || event?.raw_type || "", { compact: false }) === terminalText
  ))
  if (!terminalText || hasTerminalText) return events

  return [...events, {
    key: `diagram-${contextDiagramId(diagram)}-terminal-error`,
    text: terminalText,
    type: "error"
  }]
}

function contextDiagramWorkerStatus(diagram) {
  return String(diagram?.workerStatus ?? diagram?.worker_status ?? "").toLowerCase()
}

function contextDiagramFailureCode(diagram) {
  return String(diagram?.failureCode ?? diagram?.failure_code ?? "").toLowerCase()
}

function contextDiagramTimedOut(diagram) {
  return diagram?.status === "timed_out" ||
    contextDiagramWorkerStatus(diagram) === "timed_out" ||
    contextDiagramFailureCode(diagram) === "timed_out"
}

function contextDiagramDisplayStatus(diagram) {
  return contextDiagramTimedOut(diagram) ? "failed" : onboardingTaskDisplayStatus(diagram)
}

function contextDiagramStateLabel(diagram) {
  if (contextDiagramTimedOut(diagram)) return "Timed out"
  if (contextDiagramDisplayStatus(diagram) === "running") {
    const workerStatus = contextDiagramWorkerStatus(diagram)
    if (workerStatus === "queued") return "Queued"
    if (workerStatus === "starting") return "Starting"
    if (workerStatus === "waiting_for_user") return "Waiting"
    if (workerStatus === "cancelling") return "Stopping"
    if (workerStatus === "succeeded") return "Finalizing"
  }

  return taskStateLabel(contextDiagramDisplayStatus(diagram))
}

function contextDiagramMarkerStatus(diagram) {
  if (contextDiagramDisplayStatus(diagram) !== "running") return contextDiagramDisplayStatus(diagram)
  if (contextDiagramWorkerStatus(diagram) === "queued") return "queued"
  if (contextDiagramWorkerStatus(diagram) === "waiting_for_user") return "blocked"

  return "running"
}

function contextDiagramWorkerDetail(diagram) {
  if (contextDiagramTimedOut(diagram)) return contextDiagramFailureDetail(diagram)
  if (contextDiagramDisplayStatus(diagram) === "failed") return contextDiagramFailureDetail(diagram)
  if (contextDiagramDisplayStatus(diagram) === "running") {
    const workerStatus = contextDiagramWorkerStatus(diagram)
    if (workerStatus === "queued") return "Waiting for a diagram worker."
    if (workerStatus === "starting") return "Worker is starting. Waiting for model activity."
    if (workerStatus === "waiting_for_user") return "Worker is waiting for input."
    if (workerStatus === "cancelling") return "Stopping context diagram generation."
    if (workerStatus === "succeeded") return "Finalizing context diagram."
  }
  if (diagram?.detail) return diagram.detail
  if (contextDiagramDisplayStatus(diagram) === "queued") return "Waiting for a diagram worker."
  if (contextDiagramDisplayStatus(diagram) === "running") return "Worker is running."

  return null
}

function contextDiagramFailureTitle(diagram) {
  const label = diagram?.label || diagram?.title || diagram?.name || "Context diagram"
  if (contextDiagramTimedOut(diagram)) return `${label} timed out`
  if (contextDiagramWorkerStatus(diagram) === "cancelled" || contextDiagramFailureCode(diagram) === "cancelled") {
    return `${label} was cancelled`
  }

  return `${label} could not be generated`
}

function contextDiagramFailureDetail(diagram) {
  const detail = diagram?.error || diagram?.detail
  if (detail) return detail
  if (contextDiagramTimedOut(diagram)) return "The model did not produce a usable diagram before the deadline."
  if (contextDiagramWorkerStatus(diagram) === "cancelled" || contextDiagramFailureCode(diagram) === "cancelled") {
    return "Diagram generation was cancelled before it completed."
  }

  return "Diagram generation failed."
}

export function nextContextDiagramId({ diagrams = [], events = [], selectedDiagramId = null } = {}) {
  const availableDiagramIds = new Set(diagrams
    .filter(contextDiagramSelectable)
    .map(contextDiagramId)
    .filter(Boolean))
  if (availableDiagramIds.has(selectedDiagramId)) return selectedDiagramId

  return preferredContextDiagramId(diagrams, events)
}

function TaskEvidence({ task }) {
  const components = task.components || task.stack || []
  const outputs = task.outputs || []
  const issues = task.issues || task.findings || []
  const narratives = task.narratives || task.recentNarratives || []
  const diagrams = task.diagrams || []
  const children = task.children || task.pullRequests || []
  const current = task.processedCount ?? task.readyCount ?? task.currentCount ?? task.counts?.readyCount ?? task.count
  const total = task.totalCount ?? task.counts?.totalCount
  const exactProgressAvailable = current !== null && current !== undefined && Number.isFinite(Number(current)) && Number(total) > 0

  return (
    <>
      {exactProgressAvailable ? (
        <div className="repository-onboarding-exact-progress">
          <strong>{current} / {total}</strong>
          <div
            className="repository-onboarding-progress-track"
            role="progressbar"
            aria-label={`${task.label} processed`}
            aria-valuemin="0"
            aria-valuemax={total}
            aria-valuenow={current}
          >
            <span style={{ width: `${Math.min(100, Math.max(0, Number(current) / Number(total) * 100))}%` }} />
          </div>
        </div>
      ) : null}
      {outputs.length > 0 ? (
        <TaskStatusList items={outputs} label="Repository analysis outputs" />
      ) : null}
      {diagrams.length > 0 ? <TaskStatusList items={diagrams} label="Context diagrams" /> : null}
      {components.length > 0 ? <EvidenceList items={components} kind="stack" /> : null}
      {issues.length > 0 ? <EvidenceList items={issues} kind="issue" /> : null}
      {narratives.length > 0 ? <EvidenceList items={narratives} kind="narrative" /> : null}
      {children.length > 0 ? <PullRequestList children={children} /> : null}
      {task.error ? <p className="repository-onboarding-task-error">{task.error}</p> : null}
    </>
  )
}

function TaskStatusList({ items, label }) {
  return (
    <ol className="repository-onboarding-output-list" aria-label={label}>
      {items.map((item, index) => {
        const displayStatus = onboardingTaskDisplayStatus(item)
        return (
          <li className={`repository-onboarding-output-row is-${displayStatus}`} key={item.id || item.key || index}>
            <TaskMarker status={displayStatus} small />
            <span className="repository-onboarding-output-copy">
              <strong>{item.label || item.title || item.name}</strong>
              {item.detail ? <span>{item.detail}</span> : null}
            </span>
            <span className="repository-onboarding-output-state">{taskStateLabel(displayStatus)}</span>
          </li>
        )
      })}
    </ol>
  )
}

function EvidenceList({ items, kind }) {
  const shellRef = useRef(null)
  const [scrollable, setScrollable] = useState(false)

  useEffect(() => {
    const shell = shellRef.current
    const list = shell?.firstElementChild
    if (!shell || !list) return undefined

    const updateScrollable = () => setScrollable(list.scrollHeight > list.clientHeight + 1)
    updateScrollable()

    if (typeof ResizeObserver === "undefined") return undefined
    const observer = new ResizeObserver(updateScrollable)
    observer.observe(shell)
    observer.observe(list)
    return () => observer.disconnect()
  }, [items.length])

  return (
    <div className={`repository-onboarding-evidence-scroll is-${kind} ${scrollable ? "is-scrollable" : ""}`.trim()} ref={shellRef}>
      <ol className={`repository-onboarding-evidence-list is-${kind}`}>
        {items.map((item, index) => (
          <li className="repository-onboarding-evidence-row" key={item.id || item.key || item.sha || index}>
            <span className="repository-onboarding-evidence-mark" aria-hidden="true">
              {evidenceMark(item, kind)}
            </span>
            <span className="repository-onboarding-evidence-copy">
              <strong>{item.title || item.name || item.label}</strong>
              {evidenceDetail(item) ? (
                <span>{evidenceDetail(item)}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}

function PullRequestList({ children }) {
  return (
    <ol className="repository-onboarding-pr-list" aria-label="Automatic pull requests">
      {children.map((child, index) => {
        const displayStatus = onboardingTaskDisplayStatus(child)
        return (
          <li className={`repository-onboarding-pr-row is-${displayStatus}`} key={child.issueId || child.issue_id || index}>
            <TaskMarker status={displayStatus} small />
            <span className="repository-onboarding-pr-copy">
              <strong>{child.title}</strong>
              {child.detail ? <span>{child.detail}</span> : null}
            </span>
            {child.pullRequestUrl || child.pull_request_url ? (
              <a
                className="repository-onboarding-pr-link"
                href={child.pullRequestUrl || child.pull_request_url}
                target="_blank"
                rel="noreferrer"
              >
                PR {child.pullRequestNumber || child.pull_request_number ? `#${child.pullRequestNumber || child.pull_request_number}` : ""}
                <ExternalLink aria-hidden="true" />
              </a>
            ) : (
              <span className="repository-onboarding-pr-state">
                {child.retrying ? "Retrying" : taskStateLabel(displayStatus)}
              </span>
            )}
          </li>
        )
      })}
    </ol>
  )
}

function TaskMarker({ orbState = "working", queuedDot = false, status, small = false }) {
  const className = `repository-onboarding-task-marker ${small ? "is-small" : ""}`.trim()
  if (status === "completed") return <span className={className} aria-hidden="true"><Check /></span>
  if (status === "skipped") return <span className={className} aria-hidden="true"><Minus /></span>
  if (status === "failed") return <span className={className} aria-hidden="true"><X /></span>
  if (status === "running" && small) return <span className={className} aria-hidden="true"><i className="repository-onboarding-running-dot" /></span>
  if (status === "running") return <span className={className} aria-hidden="true"><LoadingOrb state={orbState} /></span>
  if (status === "blocked") return <span className={className} aria-hidden="true"><Clock3 /></span>
  if (status === "queued" && queuedDot) return <span className={className} aria-hidden="true"><i className="repository-onboarding-queued-dot" /></span>
  if (status === "queued") return <span className={className} aria-hidden="true"><Circle /></span>
  return <span className={className} aria-hidden="true"><CircleHelp /></span>
}

export function mergeOnboardingSnapshots(current, incoming) {
  if (!incoming) return current

  return {
    ...current,
    ...incoming,
    blocker: Object.prototype.hasOwnProperty.call(incoming, "blocker") ? incoming.blocker : null,
    completionNotificationPath:
      incoming.completionNotificationPath ??
      current?.completionNotificationPath,
    conceptsPath: incoming.conceptsPath ?? current?.conceptsPath,
    completionNotificationRequested:
      incoming.completionNotificationRequested ??
      current?.completionNotificationRequested ??
      false,
    events: mergeOnboardingEvents(current?.events, incoming.events),
    tasks: incoming.tasks?.length ? incoming.tasks : current?.tasks || []
  }
}

function normalizeSnapshot(snapshot) {
  if (!snapshot) return null
  const completionNotificationRequested =
    snapshot.completionNotificationRequested ??
    snapshot.completion_notification_requested

  return {
    ...snapshot,
    completionNotificationPath:
      snapshot.completionNotificationPath ??
      snapshot.completion_notification_path,
    completionNotificationRequested:
      completionNotificationRequested == null ?
        undefined :
        Boolean(completionNotificationRequested),
    completedCount: snapshot.completedCount ?? snapshot.completed_count,
    conceptsPath: snapshot.conceptsPath ?? snapshot.concepts_path,
    hasMore: snapshot.hasMore ?? snapshot.has_more ?? false,
    nextCursor: snapshot.nextCursor ?? snapshot.next_cursor ?? 0,
    repositoryPath: snapshot.repositoryPath ?? snapshot.repository_path,
    retryPath: snapshot.retryPath ?? snapshot.retry_path,
    runningCount: snapshot.runningCount ?? snapshot.running_count,
    overallEstimate: snapshot.overallEstimate ?? snapshot.overall_estimate,
    statusPath: snapshot.statusPath ?? snapshot.status_path,
    totalCount: snapshot.totalCount ?? snapshot.total_count,
    tasks: Array.isArray(snapshot.tasks) ? snapshot.tasks.map(normalizeTask) : [],
    events: mergeOnboardingEvents([], snapshot.events || [])
  }
}

function normalizeTask(task) {
  return {
    ...task,
    finishedAt: task.finishedAt ?? task.finished_at,
    startedAt: task.startedAt ?? task.started_at,
    processedCount: task.processedCount ?? task.processed_count,
    failureCode: task.failureCode ?? task.failure_code,
    readyCount: task.readyCount ?? task.ready_count,
    currentCount: task.currentCount ?? task.current_count,
    totalCount: task.totalCount ?? task.total_count
  }
}

function normalizeOnboardingEvent(event) {
  if (!event || event.type === "heartbeat") return null
  const cursor = Number(event.cursor || event.sequence || event.id || 0)
  const taskKey = event.taskKey || event.task_key
  const actionKey = event.actionKey || event.action_key
  const diagramId = event.diagramId ?? event.diagram_id
  const issueId = event.issueId || event.issue_id
  const rawType = event.rawType || event.raw_type
  const deltaStream = Boolean(event.deltaStream ?? event.delta_stream)
  const mergeKey = event.mergeKey || event.merge_key || null
  const text = (deltaStream || mergeKey || rawTypeLooksLikeDelta(rawType)) ?
    String(event.text || "") :
    formatEventText(event.text || rawType || "", { compact: false })
  if (!text && !event.activity) return null

  return {
    ...event,
    actionKey,
    cursor,
    createdAt: event.createdAt || event.created_at,
    deltaStream,
    diagramId,
    issueId,
    key: `${cursor || event.key || text}:${taskKey || "task"}:${diagramId ?? ""}:${issueId || ""}`,
    mergeKey,
    rawType,
    taskKey,
    text,
    type: event.type || "status"
  }
}

function onboardingEventDeltaMergeKey(event) {
  const explicitKey = event?.mergeKey || event?.merge_key
  if (explicitKey) return String(explicitKey)

  const rawType = event?.rawType || event?.raw_type
  if (!rawTypeLooksLikeDelta(rawType)) return null

  return String(rawType).replace(/(?:agent_message_delta|assistant_message_delta|message_delta|reasoning_delta|content_delta|text_delta)$/i, "delta")
}

function onboardingEventsShareDeltaStream(previous, event, mergeKey) {
  return onboardingEventDeltaMergeKey(previous) === mergeKey &&
    previous.taskKey === event.taskKey &&
    String(previous.diagramId ?? "") === String(event.diagramId ?? "") &&
    String(previous.issueId || "") === String(event.issueId || "")
}

function rawTypeLooksLikeDelta(rawType) {
  return Boolean(String(rawType || "").match(/(?:agent_message_delta|assistant_message_delta|message_delta|reasoning_delta|content_delta|text_delta)$/i))
}

function eventsForTask(events = [], taskKey) {
  return events.filter((event) => event.taskKey === taskKey)
}

function taskStateLabel(status) {
  if (status === "completed") return "Complete"
  if (status === "running") return "Running"
  if (status === "blocked") return "Waiting"
  if (status === "failed") return "Failed"
  if (status === "skipped") return "Skipped"
  if (status === "queued") return "Queued"
  return "Unknown"
}

function taskTerminal(task) {
  return terminalStatuses.has(task?.status)
}

function taskComplete(task) {
  return task?.status === "completed"
}

function taskInProgress(task) {
  return activeStatuses.has(task?.status) || task?.status === "failed"
}

function preferredOpenTask(tasks) {
  return tasks.find((task) => task.status === "running") ||
    tasks.find((task) => task.status === "blocked") ||
    tasks.find((task) => task.status === "failed") ||
    tasks.find((task) => task.status === "queued") ||
    tasks[tasks.length - 1]
}

export function onboardingFinished(snapshot, tasks = snapshot?.tasks || []) {
  return Boolean(snapshot) &&
    snapshot.active === false &&
    ["completed", "failed"].includes(snapshot.status) &&
    tasks.length > 0 &&
    tasks.every(taskTerminal)
}

function stageSummary({ completedCount, tasks }) {
  const remainingCount = tasks.filter((task) => !taskTerminal(task)).length
  const failedCount = tasks.filter((task) => task.status === "failed").length
  const skippedCount = tasks.filter((task) => task.status === "skipped").length
  const parts = []
  if (completedCount) parts.push(`${completedCount} complete`)
  if (remainingCount) parts.push(`${remainingCount} remaining`)
  if (failedCount) parts.push(`${failedCount} failed`)
  if (skippedCount) parts.push(`${skippedCount} skipped`)
  return parts.join(" · ") || "Preparing tasks"
}

function evidenceMark(item, kind) {
  if (kind === "issue") return item.severity || item.priority || "Issue"
  if (kind === "narrative") return item.shortSha || String(item.sha || item.commit || "").slice(0, 7)
  return item.monogram || String(item.name || item.label || "").slice(0, 2).toUpperCase()
}

function formatConcepts(concepts) {
  return Array.isArray(concepts) ? concepts.join(" · ") : concepts
}

function evidenceDetail(item) {
  const reportStatus = item.reportStatus || item.report_status
  const reportError = reportStatus === "failed" ? item.reportError || item.report_error : null

  return item.detail ||
    item.description ||
    item.summary ||
    item.domain ||
    item.businessDomainName ||
    formatConcepts(item.concepts || item.affectedTerms) ||
    [item.category, reportStatus || item.status, reportError].filter(Boolean).join(" · ")
}
